import { EcoaContent } from "@components/common/EconContent";
import { HbButton } from "@components/legacy/HbButton";
import { EcoaAccessCodeFlax } from "@maven-rest/cdms/ecoa/EcoaAccessCode.flax";
import { sessionSlice } from "@maven-rest/states";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { notify, useRequest } from "@coralblack/flax";
import { Footer } from "@layouts/Footer";
import { useLangContext } from "@layouts/home/LangContext";
import { ENCRYPTED_ACCESS_CODE_KEY } from "./models/identityVerification.const";
import { IDENTITY_VERIFICATION_LANG } from "./models/identityVerification.msg";
import { HbCodeInput } from "@maven-surface/components/base/HbCodeInput";
import { HbDivider } from "@maven-surface/components/base/HbDivider";
import { HbParagraph } from "@maven-surface/components/base/HbParagraph";
import { useRoute } from "@maven-surface/hooks/useRoute";
import { CDMS_STUDY_STATUS_CODE } from "@maven-msg/code/cdms/studyStatus";
import { ECOA_ACCESS_CODE_LANG } from "@maven-msg/lang/ecoaAccessCode";
import { ECOA_CAPTURE_LANG } from "@maven-msg/lang/ecoaCapture";
import { MvnMessage } from "@maven-msg/lib/Message";

const CODE_LENGTH = 6;

interface CodeVerificationProps {}

export function CodeVerification(props: CodeVerificationProps) {
  useLangContext();

  const { pushPath } = useRoute();
  const dispatch = useDispatch();
  const [accessCode, setAccessCode] = useState<string>("");

  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const { request: requestVerifyEcoaAccessCode } = useRequest(
    {
      ...EcoaAccessCodeFlax.verifyEcoaAccessCode({
        accessCode,
      }),
    },
    {
      success: (resp) => {
        handleSuccess(resp.encryptedAccessCode, resp.ecoaSpec?.twoFactorAuthType?.type);
      },
      error: handleError,
    }
  );

  function handleCodeChange(code: string) {
    setAccessCode(code);
  }

  function handleCodeFilledUp() {
    const inputs = nextButtonRef?.current?.parentElement?.getElementsByTagName("button");
    if (!inputs?.length) return;
    inputs[0]?.focus();
  }

  function handleSuccess(encryptedAccessCode: string, twoFactorAuthType: "SMS" | undefined): void {
    sessionStorage.setItem(ENCRYPTED_ACCESS_CODE_KEY, encryptedAccessCode);

    if (twoFactorAuthType === "SMS") {
      return pushPath("/auth/sms");
    }

    return pushPath("/auth/birth");
  }

  function handleError(error: any): string {
    if (error?.code === CDMS_STUDY_STATUS_CODE.STUDY_CLOSED.code) {
      dispatch(sessionSlice.actions.signOut());
    }

    if (error?.code === "ECOA_AUTH_LOCKED") {
      return MvnMessage(IDENTITY_VERIFICATION_LANG.IDENTITY_VERIFICATION_MULTIPLE_FAILED);
    }

    return MvnMessage(IDENTITY_VERIFICATION_LANG.IDENTITY_VERIFICATION_ERROR_MESSAGE);
  }

  function handleNextButtonClick(): void {
    if (accessCode.trim().length !== CODE_LENGTH) {
      return notify(MvnMessage(IDENTITY_VERIFICATION_LANG.IDENTITY_VERIFICATION_CODE_REQUIRED), "WARN");
    }

    requestVerifyEcoaAccessCode();
  }

  return (
    <>
      <EcoaContent className="app-ecoa-page-home-code-verification" verticalAlign="CENTER">
        <HbParagraph align="CENTER" margin={2} color="GRAY" text={MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_VERIFY_TEXT_LABEL)} />
        <HbDivider space={1} />
        <HbParagraph bold={true} align="CENTER">
          {MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_VERIFY_PLACEHOLDER)}
        </HbParagraph>
        <HbCodeInput length={CODE_LENGTH} code={accessCode} onValueChange={handleCodeChange} onInputCompleted={handleCodeFilledUp} />
        <HbDivider space={1} />
        <HbButton
          text={MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_INPUT_RESET_TEXT)}
          color="PRIMARY"
          disabled={!accessCode?.trim().length}
          onClick={() => setAccessCode("")}
        ></HbButton>
      </EcoaContent>
      <Footer>
        <HbButton
          elementRef={nextButtonRef}
          iconRight="ChromeBackMirrored"
          text={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_NEXT)}
          onClick={handleNextButtonClick}
        />
      </Footer>
    </>
  );
}
